<app-page-shell variant="default">
  <div class="card card-box mb-5 registration-platforms">
    <div class="card-header py-3">
      <div class="card-header--title font-size-lg">API integrations</div>
      @if (canManage()) {
      <div class="card-header--actions">
        <app-sb-button
          class="registration-platforms__add"
          variant="primary"
          size="sm"
          icon="fa fa-plus"
          label="Add"
          (clicked)="openInstall()">
        </app-sb-button>
      </div>
      }
    </div>

    <div class="p-3">
      <p class="registration-platforms__intro text-muted mb-3">Connections to your event platforms, which sync their speakers, exhibitors and attendees into this project.</p>

      @if (loadError()) {
      <div class="alert alert-danger py-2 mb-3" role="alert">{{ loadError() }}</div>
      }

      @if (platforms().length) {
      <app-sb-table
        [rows]="rows"
        [columns]="columns"
        [options]="tableOptions"
        [viewMode]="viewType">

        <div class="registration-platforms__toolbar" sbTableToolbar>
          <div class="registration-platforms__view" role="group" aria-label="View type">
            <button type="button" class="btn btn-default btn-sm list-button"
                    [class.active-btn]="viewType === 'card'" [class.inactive-btn]="viewType !== 'card'"
                    [attr.aria-pressed]="viewType === 'card'" (click)="changeViewType('card')">
              <i class="fa fa-th-large me-1" aria-hidden="true"></i>
              Card
            </button>
            <button type="button" class="btn btn-default btn-sm card-button"
                    [class.active-btn]="viewType === 'list'" [class.inactive-btn]="viewType !== 'list'"
                    [attr.aria-pressed]="viewType === 'list'" (click)="changeViewType('list')">
              <i class="fa fa-th-list me-1" aria-hidden="true"></i>
              List
            </button>
          </div>
        </div>

        <ng-template sbTableCard let-row>
          <article class="registration-platforms__card" [attr.aria-label]="platformName(asRow(row))">
            <div class="registration-platforms__card-head">
              <ng-container *ngTemplateOutlet="platformCell; context: { $implicit: row }"></ng-container>
            </div>
            <dl class="registration-platforms__card-meta">
              <div>
                <dt>Status</dt>
                <dd><ng-container *ngTemplateOutlet="statusCell; context: { $implicit: row }"></ng-container></dd>
              </div>
              <div>
                <dt>Last sync</dt>
                <dd><ng-container *ngTemplateOutlet="lastSyncCell; context: { $implicit: row }"></ng-container></dd>
              </div>
              @if (asRow(row).push?.supported) {
              <div>
                <dt>Push</dt>
                <dd><ng-container *ngTemplateOutlet="pushCell; context: { $implicit: row }"></ng-container></dd>
              </div>
              }
            </dl>
            <div class="registration-platforms__card-actions">
              <ng-container *ngTemplateOutlet="actionsCell; context: { $implicit: row }"></ng-container>
            </div>
          </article>
        </ng-template>

        <ng-template sbTableCell="platform" let-row>
          <ng-container *ngTemplateOutlet="platformCell; context: { $implicit: row }"></ng-container>
        </ng-template>
        <ng-template sbTableCell="status" let-row>
          <ng-container *ngTemplateOutlet="statusCell; context: { $implicit: row }"></ng-container>
        </ng-template>
        <ng-template sbTableCell="last_sync" let-row>
          <ng-container *ngTemplateOutlet="lastSyncCell; context: { $implicit: row }"></ng-container>
        </ng-template>
        <ng-template sbTableCell="push" let-row>
          <ng-container *ngTemplateOutlet="pushCell; context: { $implicit: row }"></ng-container>
        </ng-template>
        <ng-template sbTableCell="actions" let-row>
          <ng-container *ngTemplateOutlet="actionsCell; context: { $implicit: row }"></ng-container>
        </ng-template>
      </app-sb-table>
      }

      <!-- One template per cell, shared by the list columns and the cards. -->
      <!-- The install's name, the catalogue platform's under it when renamed, and what its plugin can push. -->
      <ng-template #platformCell let-row>
        <div class="registration-platforms__platform">
          <app-registration-platform-logo
            size="sm"
            [name]="$safeNavigationMigration(asRow(row).platform?.name)"
            [image]="$safeNavigationMigration(asRow(row).platform?.image)">
          </app-registration-platform-logo>
          <div class="registration-platforms__identity">
            <span class="registration-platforms__name">{{ platformName(asRow(row)) }}</span>
            @if (catalogueName(asRow(row))) {
            <span class="registration-platforms__catalogue">{{ catalogueName(asRow(row)) }}</span>
            }
            @if (asRow(row).platform?.capabilities?.push) {
            <span class="registration-platforms__capabilities">
              <span class="badge h-auto registration-platforms__capability">Push</span>
              @if (asRow(row).platform?.capabilities?.share_link_push) {
              <span
                class="badge h-auto registration-platforms__capability">Share link push</span>
              }
            </span>
            }
          </div>
        </div>
      </ng-template>

      <ng-template #statusCell let-row>
        <div class="registration-platforms__status">
          @if (canManage()) {
          <app-sb-input-checkbox
            #toggle
            class="registration-platforms__switch"
            mode="switch"
            layout="inline"
            [label]="asRow(row).active ? 'Active' : 'Inactive'"
            [ariaLabel]="switchLabel(asRow(row))"
            [disabled]="actingId() === asRow(row).id"
            [ngModel]="asRow(row).active"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onActiveChange(asRow(row), $event, toggle)">
          </app-sb-input-checkbox>
          } @else {
            <span class="badge h-auto" [class]="asRow(row).active ? 'text-bg-success' : 'text-bg-secondary'">
              {{ asRow(row).active ? 'Active' : 'Inactive' }}
            </span>
          }
          <ng-template #statusText>
            <span class="badge h-auto" [class]="asRow(row).active ? 'text-bg-success' : 'text-bg-secondary'">
              {{ asRow(row).active ? 'Active' : 'Inactive' }}
            </span>
          </ng-template>
          @if (!asRow(row).integration) {
          <span
            class="badge text-bg-warning h-auto registration-platforms__incomplete">Setup incomplete</span>
          }
        </div>
      </ng-template>

      <ng-template #lastSyncCell let-row>
        @if (asRow(row).last_sync) {
          <span class="registration-platforms__last-sync" [title]="lastSyncExact(asRow(row))">{{ lastSyncRelative(asRow(row)) }}</span>
          <span class="badge h-auto ms-2" [class]="syncStatusBadge(asRow(row))">{{ syncStatusLabel(asRow(row)) }}</span>
        } @else {
          <span class="text-secondary">Never synced</span>
        }
        <ng-template #neverSynced>
          <span class="text-secondary">Never synced</span>
        </ng-template>
      </ng-template>

      <!-- Push, and the share links as part of it, for a plugin that can push. -->
      <ng-template #pushCell let-row>
        @if (asRow(row).push?.supported) {
        <div class="registration-platforms__push">
          <div class="registration-platforms__push-item" data-push="push">
            <span class="registration-platforms__push-label">Push</span>
            @if (asRow(row).push?.active) {
            <span class="badge text-bg-success h-auto">On</span>
            } @else {
              <!-- Focusable, so the tooltip opens from the keyboard; the tooltip also describes it (aria-describedby). -->
              <span
                class="text-secondary registration-platforms__push-off"
                tabindex="0"
                [matTooltip]="pushOffReason(asRow(row))">Off</span>
            }
            <ng-template #pushOff>
              <!-- Focusable, so the tooltip opens from the keyboard; the tooltip also describes it (aria-describedby). -->
              <span
                class="text-secondary registration-platforms__push-off"
                tabindex="0"
                [matTooltip]="pushOffReason(asRow(row))">Off</span>
            </ng-template>
          </div>
          @if (asRow(row).push?.links_supported) {
          <div class="registration-platforms__push-item" data-push="links">
            <span class="registration-platforms__push-label">Share links</span>
            @if (asRow(row).push?.links_active) {
            <span class="badge text-bg-success h-auto">On</span>
            } @else {
              <span
                class="text-secondary registration-platforms__push-off"
                tabindex="0"
                [matTooltip]="linksOffReason(asRow(row))">Off</span>
            }
            <ng-template #linksOff>
              <span
                class="text-secondary registration-platforms__push-off"
                tabindex="0"
                [matTooltip]="linksOffReason(asRow(row))">Off</span>
            </ng-template>
          </div>
          }
        </div>
        }
      </ng-template>

      <ng-template #actionsCell let-row>
        <div class="registration-platforms__actions">
          <span
            class="registration-platforms__sync"
            matTooltip="Finish the settings to sync"
            [matTooltipDisabled]="!!asRow(row).integration">
            <app-sb-button
              variant="primary"
              size="sm"
              icon="fa fa-refresh"
              [label]="isSyncing(asRow(row)) && (loaderImport$ | async) ? 'Sync: ' + syncProgressLabel() : 'Sync'"
              [ariaLabel]="syncAriaLabel(asRow(row))"
              [loading]="isSyncing(asRow(row)) && !!(loaderImport$ | async)"
              [disabled]="!canSync(asRow(row)) || !!(loader$ | async) || !!(loaderImport$ | async)"
              (clicked)="submitInfluencerSync(asRow(row))">
            </app-sb-button>
          </span>
          <app-sb-button
            variant="outline-secondary"
            size="sm"
            icon="fa fa-clock-o"
            [iconOnly]="true"
            label="Sync history"
            matTooltip="Sync history"
            [ariaLabel]="'Sync history for ' + platformName(asRow(row))"
            (clicked)="showPreviousSyncLogs(asRow(row))">
          </app-sb-button>
          @if (canManage()) {
            <app-sb-button
              variant="outline-secondary"
              size="sm"
              icon="fa fa-pencil"
              [iconOnly]="true"
              label="Edit"
              matTooltip="Edit"
              [ariaLabel]="'Edit ' + platformName(asRow(row))"
              [disabled]="actingId() === asRow(row).id"
              (clicked)="openSettings(asRow(row))">
            </app-sb-button>
            <app-sb-button
              variant="outline-secondary"
              size="sm"
              icon="fa fa-trash"
              [iconOnly]="true"
              label="Remove"
              matTooltip="Remove"
              [ariaLabel]="'Remove ' + platformName(asRow(row))"
              [disabled]="actingId() === asRow(row).id"
              (clicked)="remove(asRow(row))">
            </app-sb-button>
          }
        </div>
      </ng-template>

      @if (loaded() && !loadError() && !platforms().length) {
      <div class="registration-platforms__empty">
        <p class="mb-3">No API integrations added yet.</p>
        @if (canManage()) {
        <app-sb-button
          variant="primary"
          icon="fa fa-plus"
          label="Add API integration"
          (clicked)="openInstall()">
        </app-sb-button>
        }
      </div>
      }
    </div>
  </div>
</app-page-shell>

<ng-template #logsTable let-modal>
  <div class="modal-header api-platform-modal-header">
    <div class="flex-grow-1 pe-3">
      <h4 class="modal-title mb-1" id="modal-title">Sync logs</h4>
    </div>
    <button type="button" class="btn-close" aria-label="Close button" aria-describedby="modal-title"
            (click)="modal.dismiss('cancel')"></button>
  </div>
  <div class="modal-body">
    @if (isImportDataFilled()) {
    <div class="import-data-container mt-4">
      <app-sb-table [rows]="importRows" [columns]="importColumns" [options]="importTableOptions">
        @for (field of importDataTableColumns(); track $index; let index = $index) {
        <ng-template [sbTableCell]="'field_' + index" let-row>{{row.record[field]}}</ng-template>
        }
      </app-sb-table>
    </div>
    }

  </div>
  <input type="hidden" ngbAutofocus>
  <div class="modal-footer border-top-0 pt-2 api-platform-modal-actions">
    <button type="button" class="btn btn-outline-platform" (click)="modal.close()">Close</button>
  </div>
</ng-template>
